import { subscribe, publish, EVENTS, cartRequest, debounce } from '@theme/global';

/**
 * Keeps the cart drawer in sync. Every mutation asks Shopify to re-render the
 * cart-drawer section and swaps the markup in, so Liquid stays the single source of truth.
 */
class CartDrawerItems extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.drawer = this.closest('theme-dialog');
    this.bind();
    subscribe(EVENTS.cartUpdate, ({ sections, source }) => {
      if (sections?.[this.sectionId]) this.replace(sections[this.sectionId]);
      if (source === 'product-form' && this.drawer && !this.drawer.dialog.open) this.drawer.open(document.activeElement);
    });
  }

  bind() {
    this.addEventListener('change', (event) => {
      const input = event.target.closest('.quantity__input');
      if (input) this.updateLine(input.dataset.line, Number(input.value));
    });
    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-remove-line]');
      if (remove) this.updateLine(remove.dataset.removeLine, 0);
    });
    const note = this.querySelector('[data-cart-note]');
    note?.addEventListener('input', debounce(() => cartRequest(window.FIVL.routes.cartUpdate, { note: note.value }), 400));
  }

  async updateLine(line, quantity) {
    this.classList.add('is-loading');
    try {
      const data = await cartRequest(window.FIVL.routes.cartChange, { line, quantity, sections: this.sectionId });
      publish(EVENTS.cartUpdate, { cart: data, sections: data.sections, source: 'cart-drawer' });
    } catch (error) {
      const status = this.querySelector('[data-cart-status]');
      if (status) status.innerHTML = `<p class="form-status form-status--error">${error?.message || window.FIVL.strings.cartError}</p>`;
      this.classList.remove('is-loading');
    }
  }

  replace(html) {
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer-items');
    if (!fresh) return;
    this.innerHTML = fresh.innerHTML;
    this.classList.remove('is-loading');
    this.bind();
  }
}
if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);

/**
 * Cart upsell: loads complementary / related products for the first cart item.
 * Re-created on every cart re-render, so it always reflects the current cart.
 */
class CartUpsell extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url) return;
    this.load();
  }
  async load(url = this.dataset.url) {
    try {
      const response = await fetch(url);
      const text = await response.text();
      const fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-upsell-content]');
      if (!fresh && url.includes('intent=complementary')) {
        // No complementary products configured: fall back to related products.
        return this.load(url.replace('intent=complementary', 'intent=related'));
      }
      this.innerHTML = fresh ? fresh.innerHTML : '';
    } catch (error) {
      this.innerHTML = '';
    }
  }
}
if (!customElements.get('cart-upsell')) customElements.define('cart-upsell', CartUpsell);
